import { useEffect, useState } from 'react';
import { app, describeError, go } from '../state/app';
import { playbackUrls } from '../api/endpoints';
import { clipKey, mediaUrl } from '../lib/media';
import { Player } from '../components/Player';
import type { PlaybackResponse } from '../api/types';

/**
 * Watch. Playback URLs are minted per view and never stored — they are the
 * walled-garden enforcement point — so this is deliberately NOT memoized: a
 * cached ephemeral URL is a dead URL an hour later.
 *
 * The URLs themselves are plain public blob links today (the container is
 * publicAccess:'Blob' with listing off, an explicit and deliberately deferred
 * hardening step), so no signing happens client-side.
 *
 * IT TAKES AN ID, NOT A VIDEO ROW, and it costs nothing to have changed: the
 * only thing this screen ever read off the Video was `videoId`, so carrying the
 * id in `/watch/<videoId>` makes the screen reachable by URL without adding a
 * single request. There is no GET /videos/{id} on the API to fetch a row with
 * anyway — the route table has the list, the approval subresource and
 * playback-urls — so a version of this that insisted on the whole object would
 * have had to refetch the entire library to find one film.
 *
 * `watchingId` is set by the library tile on the way in, and by the router from
 * the path on a cold load. Null only if something reached this screen without
 * either, which is the go('library') below.
 */
export function Watch() {
  const { watchingId } = app.use();
  const [urls, setUrls] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!watchingId) {
      go('library');
      return;
    }
    let alive = true;
    playbackUrls(watchingId)
      .then((response) => {
        if (!alive) return;
        const resolved = resolveSegments(response);
        if (!resolved.length) {
          // WRITTEN FOR THE PARENT, not for us. It used to read "The playback
          // response carried no playable segment URLs. That happens when the
          // episode has no stitched rendition and its segments could not be
          // resolved to media." — three internal nouns and no action, on the
          // screen where someone is trying to watch the thing they paid for.
          //
          // The cause is unchanged and worth keeping written down HERE: nothing
          // is signed on this deployment (the media container is public-read,
          // azure/verify_media.py proves it), so an empty result is not an
          // expired URL. It is playback_urls handing back a manifest for a
          // rendition the pipeline may never have written, plus segments
          // carrying only ids.
          setError(
            'This one will not play yet — the film is still being put together. ' +
              'Try again from your library in a few minutes.',
          );
        }
        setUrls(resolved);
      })
      .catch((err) => alive && setError(describeError(err)));
    return () => {
      alive = false;
    };
  }, [watchingId]);

  return (
    <>
      <div className="pane-img" style={{ flex: 1 }}>
        {urls && urls.length > 0 ? (
          // autoStart, but with lower expectations than the child shelf has.
          // The gesture that could pay for playback happened on the library
          // tile, in screens/Library.tsx, and this screen is not mounted until
          // a render and a network round trip later — so there is nothing left
          // here to carry. Browsers that gate autoplay on engagement with the
          // origin rather than on a live gesture (Chrome, Edge, Android) simply
          // play; iOS Safari refuses, and Player puts its ▶︎ back, which is
          // exactly the two-tap flow this screen has always had. Making the
          // library tile mount the player the way ChildMode does is the fix,
          // and it belongs in Library.tsx.
          <Player urls={urls} autoStart onExit={() => go('library')} />
        ) : (
          <div className="placeholder">
            {error ? '' : 'minting playback URLs…'}
            {error && <div className="err">{error}</div>}
            <br />
            <button className="ghost" onClick={() => go('library')}>
              back to the library
            </button>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Resolve a playlist out of whatever the API returned, most explicit first.
 * Everything is keys-to-URLs through lib/media so the signing story stays in
 * one place.
 */
function resolveSegments(response: PlaybackResponse): string[] {
  if (Array.isArray(response.segmentUrls) && response.segmentUrls.length) {
    return response.segmentUrls.filter(Boolean);
  }

  const fromSegments = (response.segments ?? [])
    .map((segment) => {
      if (segment.url) return segment.url;
      const key = segment.storageKey ?? segment.key;
      if (key) return mediaUrl(key);
      // Documented seed convention: clips/<assetId>/master.mp4.
      return segment.segmentId ? mediaUrl(clipKey(segment.segmentId)) : null;
    })
    .filter((url): url is string => Boolean(url));

  if (fromSegments.length) return fromSegments;

  // Last resort: a single HLS manifest. Safari plays it natively; other
  // browsers will not, which the player surfaces as a load error.
  return response.hlsUrl ? [response.hlsUrl] : [];
}
